<template>
  <ModalComponent
    name="createOrUpdateMetric"
    height="auto"
    width="900"
    :shift-x="createOrUpdateMetricShiftX"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="false"
    @opened="modalOpened = true"
    @closed="
      invalidUrlMessage = null;
      modalOpened = false;
    "
  >
    <form
      class="z-[99999] px-8 py-6"
      @submit.prevent="saveChanges"
    >
      <div
        class="flex items-center justify-between gap-x-[300px] border-b border-[#EDEDED] pb-4"
      >
        <div class="gap-1.5">
          <h3 class="text-lg font-semibold text-primary lg:text-xl">
            {{ selected.metric_id ? "Edit" : "Add New" }} Metric
          </h3>
          <p class="whitespace-nowrap text-sm text-primary/50">
            Please fill all the fields to
            {{ selected.metric_id ? "edit" : "create" }} the metric
          </p>
        </div>
        <div class="flex items-center gap-6">
          <div class="flex items-center gap-1">
            <span class="text-sm font-medium">Draft</span>
            <button
              type="button"
              :class="{
                'bg-emerald-600': selected.status === 'published',
                'bg-gray-200': selected.status === 'draft',
              }"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500"
              role="switch"
              :aria-checked="selected.status === 'published'"
              @click="toggleStatus"
            >
              <span class="sr-only">Toggle published status</span>
              <span
                aria-hidden="true"
                :class="{
                  'translate-x-5': selected.status === 'published',
                  'translate-x-0': selected.status === 'draft',
                }"
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              />
            </button>
            <span class="text-sm font-medium">Published</span>
          </div>
          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-md bg-[#EEEFF0D4] text-[#808080] outline-none"
            @click="hideMetricModal"
          >
            <TimesSmall />
          </button>
        </div>
      </div>
      <div class="mb-5">
        <div class="form-group flex gap-2">
          <div class="w-full py-3">
            <div class="flex items-center gap-8">
              <div class="flex w-full flex-col gap-y-1">
                <label class="text-sm font-medium">Metric name <span class="text-red-600">*</span></label>
                <TextInput
                  type="text"
                  :val="selected.metric_name"
                  :required="true"
                  :placeholder="`Please enter metric name`"
                  @input="selected.metric_name = $event"
                  @blur="$emit('validateMetricName')"
                />
                <small
                  v-if="errorMessage"
                  class="mb-2 mt-1 block font-medium text-red-primary"
                >{{ errorMessage }}</small>
              </div>
              <div class="flex w-full flex-col gap-1 py-4">
                <label class="text-sm font-medium">Topic <span class="text-red-600">*</span></label>
                <TextInput
                  v-if="selected.metric_id"
                  id="asset-name"
                  type="text"
                  :val="selected.topic.title"
                  :required="true"
                  :placeholder="`Topic name`"
                  :readonly="true"
                />
                <DropDownInput
                  v-else
                  display-key="title"
                  value-key="id"
                  empty-selection-text="Select topic"
                  :options="sections"
                  max-width-class="max-w-full"
                  :is-required="true"
                  @value-updated="selected.metric_topic = $event"
                />
              </div>
            </div>
            <div class="flex items-center gap-8">
              <div class="flex w-[calc(50%-16px)] flex-col gap-1 pb-2">
                <label class="text-sm font-medium">Pre-configuration</label>
                <DropDownInput
                  display-key="ui_name"
                  value-key="name"
                  :first-value="selected.config_name"
                  :options="configData"
                  :is-required="true"
                  max-width-class="max-w-full"
                  @value-updated="selected.config_name = $event"
                />
              </div>
              <div class="flex w-[calc(50%-16px)] flex-col gap-1 pb-2">
                <label class="text-sm font-medium">Data Aggregation Type
                  <span class="text-red-600">*</span></label>
                <DropDownInput
                  display-key="name"
                  value-key="id"
                  :first-value="selected.data_aggregation_type"
                  :options="dataAggregations"
                  :is-required="true"
                  max-width-class="max-w-full"
                  @value-updated="selected.data_aggregation_type = $event"
                />
              </div>
            </div>
            <div
              v-if="selected.metric_id"
              class="flex flex-col gap-1 py-2"
            >
              <label class="text-sm font-medium">Framework</label>
              <TextInput
                type="text"
                :val="selected.framework"
                :required="true"
                :placeholder="`No Framework Data`"
                :readonly="true"
              />
            </div>
            <div
              v-if="selected.metric_id"
              class="flex flex-col gap-1 py-2"
            >
              <label class="text-sm font-medium">Framework Index</label>
              <TextInput
                type="text"
                :val="selected.framework_index"
                :required="true"
                :placeholder="`No Framework Index Data`"
                :readonly="true"
              />
            </div>
            <div class="mt-3 flex w-full flex-col gap-2">
              <label class="text-sm font-medium">Information Guidance</label>
              <TinyEditor
                :id="`framework-description`"
                v-model="selected.information_guidance"
                :api-key="$config.public.tinyKey"
                cloud-channel="4"
                :init="tinyInit"
              />
            </div>
            <div class="mt-3 w-full">
              <label class="mb-1.5 block text-sm font-medium text-primary">Tutorial Video (Embed Link)
              </label>
              <input
                id="link"
                v-model="selected.tutorial_video"
                class="text-md border-1 focus:shadow-outline mt-3 block w-full rounded-sm border-gray-300 bg-white px-6 py-3 leading-tight text-primary placeholder-slate-300 shadow-sm focus:border-main-primary focus:ring-main-primary"
                type="url"
                :placeholder="`Please enter the url`"
                @change="validateUrl"
              >
              <p
                v-if="invalidUrlMessage"
                class="p-3 text-sm text-red-500"
              >
                {{ invalidUrlMessage }}
              </p>
            </div>
            <div class="mt-3 w-full">
              <div class="w-full flex items-center justify-between">
                <label class="block text-sm font-medium text-primary">Metric Table</label>
                <label class="space-x-2">
                  Or
                  <span class="sr-only">Import Excel</span>
                  <input
                    type="file"
                    accept=".xlsx"
                    class="text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-slate-50 file:py-2 file:text-sm file:font-semibold file:text-main-primary hover:file:bg-main-primary/10"
                    @change="handleExcelUpload"
                  >
                </label>
              </div>
              <div class="mt-2 flex h-fit w-full items-center gap-2">
                <div
                  ref="metricTableContainer"
                  class="grow"
                >
                  <div class="relative w-full">
                    <span
                      class="absolute left-0 top-0 z-10 h-full w-[50px] bg-main-primary px-3 py-2 text-xs text-slate-50"
                    >&fpartint;&xscr;</span>
                    <div
                      v-if="activeCellFormula && activeCellFormula.startsWith('=')"
                      class="absolute right-0 top-0 z-10 flex h-full items-center gap-1.5 px-3 py-1"
                    >
                      <button
                        type="button"
                        class="text-green-600 outline-none"
                        @click="applyNewCellFormula"
                      >
                        &checkmark;
                      </button>
                      <button
                        type="button"
                        class="text-red-600 outline-none"
                        @click="clearCellFormula"
                      >
                        &cross;
                      </button>
                    </div>
                    <input
                      ref="activeCellFormula"
                      :value="activeCellFormula || ''"
                      readonly
                      type="text"
                      class="box-border w-full border-gray-200 bg-white py-1 pl-14 pr-3 text-sm text-slate-400 outline-none focus:border-main-primary focus:ring-1 focus:ring-main-primary"
                    >
                  </div>
                  <hot-table
                    v-if="canViewTable"
                    ref="metricTable"
                    :settings="hotSettings"
                  />
                </div>
                <button
                  type="button"
                  class="flex-none scale-[-1] bg-slate-50 px-1 py-3 text-xs text-main-primary shadow-md transition-all [writing-mode:vertical-lr] hover:translate-y-0.5"
                  @click="addMetricColumn"
                >
                  New Column
                </button>
              </div>
              <button
                type="button"
                class="mx-auto mt-2 block bg-slate-50 px-3 py-1 text-xs text-main-primary shadow-md transition-all hover:-translate-y-0.5"
                @click="addMetricRow"
              >
                New Row
              </button>
            </div>
          </div>
        </div>
        <hr class="mb-4 mt-4">
        <div class="flex justify-end gap-3">
          <div class="flex items-center justify-end gap-2.5">
            <GhostButton @click="hideMetricModal">
              Cancel
            </GhostButton>
            <PrimaryButton
              type="submit"
              :disabled="!canSaveMetric"
              aria-label="Save"
            >
              Save
            </PrimaryButton>
          </div>
        </div>
      </div>
    </form>
  </ModalComponent>
</template>

<script>
import ExcelJS from 'exceljs'
import { HyperFormula } from 'hyperformula'
import Handsontable from 'handsontable'

export default {
  name: 'CrudMetricForm',

  props: {
    selectedMetric: {
      type: Object,
      default: () => {},
    },
    error: {
      type: String,
      default: '',
    },
    sections: {
      type: Array,
      required: true,
    },
    configData: {
      type: Array,
      required: true,
    },
  },

  emits: ['reload', 'validateMetricName'],

  data() {
    return {
      selected: {},
      invalidUrlMessage: null,
      errorMessage: this.error,
      dataAggregations: [
        { name: 'Average', id: 'average' },
        { name: 'None', id: 'none' },
        { name: 'Sum', id: 'sum' },
      ],
      activeCellFormula: null,
      modalOpened: false,
      lastActiveCell: null,
      highlighted: false,
    }
  },

  computed: {
    hotSettings() {
      return {
        data: this.selected.metric_table_data,
        contextMenu: true,
        rowHeaders: true,
        colHeaders: true,
        licenseKey: this.$config.public.handsontableLicenseKey,
        height: 'auto',
        className: 'htLeft',
        mergeCells: this.selected.mergedCells.length
          ? this.selected.mergedCells
          : true,
        formulas: {
          engine: HyperFormula.buildEmpty({
            licenseKey: this.$config.public.hyperformulaLicenseKey,
          }),
          sheetName: 'createOrUpdateMetric',
        },
        colWidths: [...this.selected.metric_table_data[0]].map(() => {
          const numberOfCols = this.selected.metric_table_data[0].length
          return Math.round(
            this.$refs.metricTableContainer.offsetWidth / numberOfCols,
          )
        }),
        afterLoadData: () => {
          if (!this.highlighted) {
            setTimeout(() => this.highlightFormulas(), 100)
            this.highlighted = true
          }
        },
        afterSelection: (row, col) => {
          const value = this.selected.metric_table_data[row][col]
          this.syncExternalInput(row, col, value)
        },
        afterBeginEditing: () => {
          const editor = this.$refs.metricTable.hotInstance.getActiveEditor()
          if (editor && editor.TEXTAREA) {
            // Attach an input listener to update external input live
            this.onEditorInput = () => {
              this.activeCellFormula = editor.TEXTAREA.value
            }
            editor.TEXTAREA.addEventListener('input', this.onEditorInput)
          }
        },
        afterEndEditing: () => {
          const editor = this.$refs.metricTable.hotInstance.getActiveEditor()
          if (editor && editor.TEXTAREA) {
            // Remove the input listener to clean up
            editor.TEXTAREA.removeEventListener('input', this.onEditorInput)
            this.onEditorInput = null
          }
        },
      }
    },
    createOrUpdateMetricShiftX() {
      return window.innerWidth > 1500 ? 0.5 : 0.8
    },
    canViewTable() {
      return this.selected?.metric_table_data?.length && this.modalOpened
    },
    tinyPlugins() {
      return `advlist autolink lists link image charmap preview anchor pagebreak searchreplace wordcount visualblocks visualchars code fullscreen insertdatetime media nonbreaking save table code directionality emoticons ${this.$config.public.tinyLive ? 'tinydrive advtable powerpaste' : ''}`
    },
    tinyInit() {
      const tinyIsLive = this.$config.public.tinyLive

      return {
        height: 200,
        image_caption: true,
        image_title: true,
        automatic_uploads: false,
        ...(this.$config.public.tinyLive && {
          tinydrive_token_provider: this.$config.public.tinydriveTokenProvider,
        }),
        toolbar: `undo redo | bold italic styles | link image ${tinyIsLive ? 'insertfile' : ''} | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent paste`,
        paste_webkit_styles: 'all',
        paste_remove_styles_if_webkit: false,
        smart_paste: true,
        image_file_types: 'jpg,jpeg,svg,webp,png,gif',
        paste_merge_formats: true,
        file_picker_types: 'image',
        // use a native confirm dialog to prompt the user to choose between clean and merge
        powerpaste_word_import: () =>
          new Promise(resolve =>
            confirm('Would you like to keep formatting?')
              ? resolve('merge')
              : resolve('clean'),
          ),
        powerpaste_googledocs_imports: () =>
          new Promise(resolve =>
            confirm('Would you like to keep formatting?')
              ? resolve('merge')
              : resolve('clean'),
          ),
      }
    },
    canSaveMetric() {
      if (
        !this.selected.metric_name
        || !this.selected.metric_topic
        || !this.selected.data_aggregation_type
      ) {
        return false
      }
      return this.invalidUrlMessage === null && !this.errorMessage
    },
  },

  watch: {
    selectedMetric(newSelectedMetric) {
      this.selected = JSON.parse(JSON.stringify(newSelectedMetric))
    },
    selected(newMetric) {
      if (Object.keys(newMetric).length) {
        this.$modal.show('createOrUpdateMetric')
      }
    },
    error(newErrorMessage) {
      this.errorMessage = newErrorMessage
    },
  },

  methods: {
    syncExternalInput(row, col, value) {
      const hfInstance = this.$refs.metricTable.hotInstance.getPlugin('formulas').engine
      const sheetId = hfInstance.getSheetId('createOrUpdateMetric')
      const cellAddress = { row, col, sheet: sheetId }

      if (hfInstance.doesCellHaveFormula(cellAddress)) {
        this.activeCellFormula = hfInstance.getCellFormula(cellAddress)
      }
      else {
        this.activeCellFormula = value !== null ? value : ''
      }
    },
    highlightFormulas() {
      const hotInstance = this.$refs.metricTable.hotInstance
      const engine = hotInstance.getPlugin('formulas').engine

      highlightCellsWithFormulas(
        this.selected.metric_table_data,
        hotInstance,
        engine,
        'createOrUpdateMetric',
      )
    },
    applyNewCellFormula() {
      if (this.activeCellFormula && this.lastActiveCell) {
        const { row, col } = this.lastActiveCell
        const hfInstance
          = this.$refs.metricTable.hotInstance.getPlugin('formulas').engine
        const sheetId = hfInstance.getSheetId('createOrUpdateMetric')
        const cellAddress = { col, row, sheet: sheetId }

        if (!hfInstance.validateFormula(this.activeCellFormula)) {
          this.$toast.error(
            `Please make sure you have set a valid formula! Received: ${this.activeCellFormula}`,
            {
              timeout: 5000,
              position: 'top-right',
            },
          )
          return
        }

        hfInstance.setCellContents(cellAddress, this.activeCellFormula)
        this.highlightCells()
      }
    },
    clearCellFormula() {
      this.highlightCells()
      this.activeCellFormula = null
    },
    highlightCells() {
      const hotInstance = this.$refs.metricTable.hotInstance
      highlightCellsWithFormulas(
        hotInstance.getData(),
        hotInstance,
        hotInstance.getPlugin('formulas').engine,
        'createOrUpdateMetric',
      )
    },
    addMetricColumn() {
      this.$refs.metricTable.hotInstance.alter('insert_col_end')

      const newInstance = this.$refs.metricTable.hotInstance
      // Scrollable section
      const hotContainer = newInstance.view._wt.wtOverlays.scrollableElement
      hotContainer.scrollLeft = hotContainer.scrollWidth
    },
    addMetricRow() {
      this.$refs.metricTable.hotInstance.alter('insert_row_below')
    },
    async handleExcelUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      try {
        const workbook = await this.readXlsxWorkbookFromFile(file)
        const sheetsAsJavascriptArrays = this.convertXlsxWorkbookToJavascriptArrays(workbook)
        const hf = HyperFormula.buildFromSheets(sheetsAsJavascriptArrays, { licenseKey: this.$config.public.hyperformulaLicenseKey })
        const formulas = hf.getSheetSerialized(0)

        this.updateMetricTableData(formulas)

        nextTick(() => {
          if (this.$refs.metricTable) {
            // Destroy the Handsontable instance before recreating it
            this.$refs.metricTable.hotInstance.destroy()
          }

          // Use nextTick to ensure DOM updates before recreating
          nextTick(() => {
            this.$refs.metricTable.hotInstance = new Handsontable(this.$refs.metricTableContainer, {
              ...this.hotSettings,
              data: this.selected.metric_table_data,
            })
          })
        })
      }
      catch (error) {
        this.$toast.error(`Error processing file: ${error}`)
      }
    },
    updateMetricTableData(formulas) {
      if (!Array.isArray(formulas)) return

      this.selected.metric_table_data = [...formulas]

      // If mergedCells exist, remove them (temporary solution)
      if (this.selected.mergedCells && this.selected.mergedCells.length) {
        this.selected.mergedCells = []
      }
    },
    async readXlsxWorkbookFromFile(file) {
      const workbook = new ExcelJS.Workbook()
      const reader = new FileReader()

      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          const buffer = e.target.result
          await workbook.xlsx.load(buffer)
          resolve(workbook)
        }
        reader.onerror = error => reject(error)
        reader.readAsArrayBuffer(file)
      })
    },
    convertXlsxWorkbookToJavascriptArrays(workbook) {
      const workbookData = {}

      workbook.eachSheet((worksheet) => {
        const sheetData = []

        worksheet.eachRow((row) => {
          const rowData = []

          row.eachCell((cell) => {
            const cellData = cell.formula ? `=${cell.formula}` : cell.value
            rowData.push(cellData)
          })

          sheetData.push(rowData)
        })

        workbookData[worksheet.name] = sheetData
      })

      return workbookData
    },
    validateUrl(e) {
      this.selected.tutorial_video = e.target.value
      const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/

      if (!urlPattern.test(this.selected.tutorial_video)) {
        this.invalidUrlMessage
          = 'Error: Invalid url added. Please enter a valid url.'
      }
      else {
        this.invalidUrlMessage = null
      }
    },
    hideMetricModal() {
      this.errorMessage = ''
      this.$refs.metricTable.hotInstance.destroy()
      this.activeCellFormula = null
      this.lastActiveCell = null
      this.$modal.hide('createOrUpdateMetric')
      this.selected = {}
    },
    toggleStatus() {
      if (this.selected.status === 'draft') {
        this.selected.status = 'published'
      }
      else {
        this.selected.status = 'draft'
      }
    },
    saveChanges() {
      if (this.selected.config_name) {
        this.selected.pre_configuration = [...this.configData].find(
          ({ name }) => name === this.selected.config_name,
        )
      }

      const instance = this.$refs.metricTable.hotInstance
      const metricTableData = {
        data: instance.getData(),
        mergedCells:
          instance.getPlugin('mergeCells').mergedCellsCollection.mergedCells,
        cellFormulas: retrieveFormulas(
          instance,
          instance.getPlugin('formulas').engine,
          'createOrUpdateMetric',
        ),
      }

      metricTableData.cellFormulas.forEach(({ row, col, formula }) => {
        metricTableData.data[row][col] = formula
      })

      const { data: tableData, mergedCells } = metricTableData

      if (this.selected.metric_id) {
        const {
          metric_name,
          templates,
          pre_configuration,
          data_aggregation_type,
          status,
          information_guidance,
          tutorial_video,
        } = { ...this.selected }
        const updateData = {
          metric_name,
          templates,
          pre_configuration,
          data_aggregation_type,
          status,
          information_guidance,
          tutorial_video,
          metric_table: { data: tableData, mergedCells },
        }
        delete updateData.config_name
        delete updateData.metric_table_data

        this.$apis.allTenants
          .updateMetric(this.selected.metric_id, updateData)
          .then(() => {
            this.hideMetricModal()
            this.$toast.success(`Metric has been updated`, {
              timeout: 3000,
              position: 'top-right',
            })
            this.$emit('reload')
          })
      }
      else {
        const createData = {
          ...this.selected,
          metric_table: { data: tableData, mergedCells },
        }
        delete createData.config_name
        delete createData.metric_table_data

        this.$apis.allTenants.createMetric(createData).then(() => {
          this.hideMetricModal()
          this.$toast.success(`Successful new Metric have been  added`, {
            timeout: 3000,
            position: 'top-right',
          })
          this.$emit('reload')
        })
      }
    },
  },
}
</script>

<style>
.handsontable {
z-index: 9999999 !important;
  }
</style>
